import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, FileSpreadsheet, Upload } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { ActionModuleResponse, ImportActionModuleScoresRequest } from '@/api/model'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { cn } from '@/lib/utils'
import { notify } from '@/lib/notify'
import { readSheetAsRows } from '@/lib/readSpreadsheet'
import { usePostApiCompaniesCurrentActionmodulesActionModuleIdImport } from '@/api/generated/company-management-user/company-management-user'
import { getWeekRange, toIso } from '@/pages/incentivation/lib'
import { moduleIncentivationDataType } from '../lib'
import { parseIncentivationExcel } from './lib/parseIncentivationExcel'
import { ImportPreviewDialog } from './ImportPreviewDialog'

const FILE_PATTERN = /\.(xlsx|csv)$/i
const { start: DEFAULT_START, end: DEFAULT_END } = getWeekRange()

/**
 * Functional Excel-upload area: pick/drop a file → parse (xlsx) → preview → confirm →
 * POST the scores for the chosen period. The period pickers set the import's date range
 * (the endpoint requires start/end), defaulting to the current week like the old portal.
 * Only mounted for Manual modules that allow bulk upload.
 */
export function ExcelImportCard({ module }: { module: ActionModuleResponse }) {
  const { t } = useTranslation('action-modules-detail')
  const moduleId = module.id ?? 0
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [start, setStart] = useState(DEFAULT_START)
  const [end, setEnd] = useState(DEFAULT_END)
  const [dragging, setDragging] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const [pendingRows, setPendingRows] = useState<ImportActionModuleScoresRequest[] | null>(null)

  const { mutateAsync, isPending } = usePostApiCompaniesCurrentActionmodulesActionModuleIdImport()

  async function handleFile(file: File) {
    if (!FILE_PATTERN.test(file.name)) {
      notify.error(t('importExport.toasts.wrongType'))
      return
    }
    let rows
    try {
      rows = await readSheetAsRows(file)
    } catch {
      notify.error(t('importExport.toasts.readError'))
      return
    }

    const parsed = parseIncentivationExcel(rows, moduleIncentivationDataType(module))
    if (parsed.length === 0) {
      notify.error(t('importExport.toasts.noData'))
      return
    }
    setFileName(file.name)
    setPendingRows(parsed)
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) void handleFile(file)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  async function handleConfirm() {
    if (!pendingRows) return
    try {
      await mutateAsync({
        actionModuleId: moduleId,
        params: { startDate: toIso(start), endDate: toIso(end, true) },
        data: pendingRows,
      })
      // Refresh every employees/instances query of this module.
      const prefix = `/api/companies/current/actionmodules/${moduleId}/employees`
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix),
      })
      notify.success(t('importExport.toasts.success'))
    } catch (error) {
      notify.apiError(error, t('importExport.toasts.error'))
    } finally {
      setPendingRows(null)
      setFileName(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('importExport.excelUpload.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">{t('incentivation.filters.from')}</Label>
          <div className="relative">
            <Calendar className="absolute left-2.5 top-2 size-4 text-muted-foreground" />
            <Input type="date" className="h-9 w-40 pl-8 text-sm" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">{t('incentivation.filters.to')}</Label>
          <div className="relative">
            <Calendar className="absolute left-2.5 top-2 size-4 text-muted-foreground" />
            <Input type="date" className="h-9 w-40 pl-8 text-sm" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onInputChange} />
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:bg-muted/40',
          dragging && 'border-primary bg-muted/40',
        )}
      >
        {fileName ? (
          <>
            <FileSpreadsheet className="size-8 text-primary" />
            <span className="text-sm font-medium">{fileName}</span>
          </>
        ) : (
          <>
            <Upload className="size-8 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('importExport.excelUpload.hint')}</span>
            <span className="text-xs text-muted-foreground">{t('importExport.excelUpload.dropOrClick')}</span>
          </>
        )}
      </div>

      <ImportPreviewDialog
        open={pendingRows !== null}
        rows={pendingRows ?? []}
        isUploading={isPending}
        onConfirm={handleConfirm}
        onCancel={() => { setPendingRows(null); setFileName(null) }}
      />
      </CardContent>
    </Card>
  )
}
